/**
 * @module
 * @author Jacob Viertel <jv@onscreen.net>
 * @since 3.84.0
 */

import {AttributeSetup} from '@acng/frontend-stargazer';
import {join} from '@acng/frontend-bounty/std/array.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';

import {POSSIBLE_FSK_KEYS} from '../../core/model/fsk.js';
import {make} from '../../core/service/compare.js';
import {portal} from '../../core/portal.js';
import {authUser} from '../context/auth-user.js';
import {mapDeprecatedAttribute} from '../../core/service/debug.js';

/**
 * Remove the element if the fsk condition is not met.
 *
 * @example
 * ```html
 * <div user-fsk="18">
 *   <!-- The div is removed if users fsk level is smaller than 18. -->
 * </div>
 * ```
 *
 * An optional comparison operator can be prefixed to the fsk level to be more specific.
 *
 * ```html
 * <div user-fsk="= 12"></div>
 * <div user-fsk="< 18"></div>
 * <div user-fsk=">= 16"></div>
 * ```
 *
 * ## Formal Syntax
 * ```
 * user-fsk = [ <operator> ] <fsk-level>
 * <operator> = '<' | '<=' | '=' | '>=' | '>'
 * <fsk-level> = '12' | '16' | '18'
 * ```
 * @group DOM Attribute
 */

const USER_FSK = 'user-fsk';

AttributeSetup(USER_FSK, () => {
  const compare = make(join(POSSIBLE_FSK_KEYS, '|'));

  return (element, value) => {
    const dropIt = !compare(value, authUser?.fsk ?? portal.guest_fsk);

    DEBUG: if (debug(element)) {
      if (dropIt) {
        console.log(USER_FSK, 'remove', element);
      } else {
        console.debug(USER_FSK, 'keep', element);
      }
    }
    if (dropIt) {
      remove(element);
    }
  };
});

AttributeSetup('data-fsk', mapDeprecatedAttribute(USER_FSK));
